import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, memo, useEffect, useMemo, useRef } from 'react';
import { offset } from '@floating-ui/core';
import { useFloating, useInteractions, useClick, useDismiss, useHover, arrow as arrowMiddleware, FloatingArrow, autoUpdate, useFocus, hide, } from '@floating-ui/react';
import ReactDOM from 'react-dom';
import { useTheme } from 'styled-components';
import { useControlledState } from '@/js/libs/hooks/useControlledState';
import { useMergeRefs } from '@/js/libs/hooks/useMergeRefs';
import { getFloatingPlacement } from './getFloatingPlacement';
import { Content, Trigger, Wrapper } from './styled';
import { useExtendedClick } from './useClickExtended';
export const Tooltip = memo(forwardRef(function Tooltip({ children, className, tooltip, renderTooltipWithoutWrapper = false, WrapperComponent = Wrapper, disabled, visible, closeOnTriggerHidden, dataTestId, containerComponentProps, trigger = 'hover', zIndex, placement = 'auto', onVisibleChange, ContainerComponent = Trigger, type = 'tooltip', autoSize = false, contentPadding = true, delayShow = 100, delayHide = 100, arrow = true, backgroundColor, }, ref) {
    const [isVisible, setIsVisible] = useControlledState(visible, false, onVisibleChange);
    const triggers = useMemo(() => {
        let triggersList = Array.isArray(trigger) ? trigger : [trigger];
        if (typeof window !== 'undefined') {
            const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
            if (isTouchDevice) {
                // Not all touch devices can't do right click, so replace it with click
                triggersList = triggersList.reduce((acc, t) => {
                    acc.push(t === 'right-click' ? 'click' : t);
                    return acc;
                }, new Array());
            }
        }
        return triggersList;
    }, [trigger]);
    const theme = useTheme();
    const [defaultPlacement, placementMiddleware] = getFloatingPlacement(placement);
    const arrowRef = useRef(null);
    const { refs, floatingStyles, context, middlewareData } = useFloating({
        placement: defaultPlacement,
        open: isVisible,
        onOpenChange: setIsVisible,
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(5),
            placementMiddleware,
            // eslint-disable-next-line react-hooks/refs
            arrowMiddleware({
                element: arrowRef,
                padding: theme.borders.radiusMedium,
            }),
            closeOnTriggerHidden && hide(),
        ],
    });
    const containerRef = useMergeRefs(refs.setReference, ref);
    // This property added by hide middleware.
    // https://floating-ui.com/docs/hide#referencehidden
    const refHidden = middlewareData.hide?.referenceHidden;
    useEffect(() => {
        if (refHidden) {
            setIsVisible(false);
        }
    }, [refHidden, setIsVisible]);
    const leftClick = useClick(context, {
        event: 'click',
        enabled: triggers.includes('click'),
    });
    const rightClick = useExtendedClick(context, {
        event: 'right-click',
        enabled: triggers.includes('right-click'),
    });
    const hover = useHover(context, {
        delay: {
            open: delayShow,
            close: delayHide,
        },
        enabled: triggers.includes('hover'),
    });
    const focus = useFocus(context, {
        enabled: triggers.includes('focus'),
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([leftClick, hover, rightClick, focus]);
    useDismiss(context, {
        enabled: isVisible,
    });
    return disabled || !tooltip ? (_jsx(ContainerComponent, { className: className, ref: ref, "data-testid": dataTestId, ...containerComponentProps, children: children })) : (_jsxs(_Fragment, { children: [isVisible &&
                ReactDOM.createPortal(_jsxs(WrapperComponent, { zIndex: zIndex, ref: refs.setFloating, ...getFloatingProps(), style: {
                        ...floatingStyles,
                        ...(backgroundColor ? { background: backgroundColor } : {}),
                    }, type: type, "data-testid": 'tooltip-wrapper', children: [renderTooltipWithoutWrapper ? (tooltip) : (_jsx(Content, { contentPadding: contentPadding, autoSize: autoSize, children: tooltip })), arrow && (_jsx(FloatingArrow, { ref: arrowRef, context: context, fill: backgroundColor ? backgroundColor : type === 'tooltip' ? theme.colors.black_1 : theme.colors.grey00_1, width: 11, height: 5 }))] }), document.body), _jsx(ContainerComponent, { "data-testid": dataTestId, ...containerComponentProps, className: className, ref: containerRef, ...getReferenceProps(), children: children })] }));
}));
// https://www.figma.com/file/OPrQYe21YjiU2kmoGd571W/AdConsole---Design-System?node-id=648%3A2778
export const DelayedTooltip = (props) => _jsx(Tooltip, { ...props, delayShow: 300, delayHide: 500 });
